/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Trash2, Calendar, UserRound, Download, History } from 'lucide-react';
import { downloadNotePdf } from '@/components/voicediary/pdfExport';

const formatDateTime = (iso) => {
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso || ''; }
};

const listFields = ['activities', 'critical_issues', 'follow_up'];
const textFields = ['context', 'patient_condition', 'vital_signs_notes', 'food_hydration', 'elimination', 'mobility', 'mood_behavior', 'notes_extra'];

export const DiaryNoteCard = ({ note, onDelete, onOpenTimeline, schema = [] }) => {
  const [open, setOpen] = useState(false);
  const s = note.structured || {};

  const labelFor = (key) => (schema.find((f) => f.key === key)?.label || key);

  const summaryChips = [];
  if (s.critical_issues?.length) summaryChips.push({ label: 'criticità', variant: 'destructive' });
  if (s.follow_up?.length) summaryChips.push({ label: 'follow-up', variant: 'default' });
  if (s.vital_signs_notes) summaryChips.push({ label: 'parametri', variant: 'secondary' });

  const hasPatient = !!note.patient_id;

  return (
    <Card data-testid={`diary-note-${note.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {note.patient_label && (
                hasPatient ? (
                  <button
                    type="button"
                    onClick={() => onOpenTimeline?.({ id: note.patient_id, label: note.patient_label })}
                    className="flex items-center gap-1 text-sm font-semibold rounded-md px-1.5 -mx-1.5 hover:bg-primary/10 transition-colors"
                    data-testid={`open-timeline-${note.id}`}
                    title="Apri cronologia paziente"
                  >
                    <UserRound className="h-3.5 w-3.5 text-primary" />
                    <span className="underline decoration-primary/30 decoration-2 underline-offset-2">{note.patient_label}</span>
                    <History className="h-3 w-3 text-muted-foreground ml-0.5" />
                  </button>
                ) : (
                  <div className="flex items-center gap-1 text-sm font-semibold">
                    <UserRound className="h-3.5 w-3.5 text-primary" />
                    {note.patient_label}
                  </div>
                )
              )}
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Calendar className="h-3 w-3" /> {formatDateTime(note.created_at)}
              </div>
            </div>
            {s.context && (
              <p className="text-sm mt-1 line-clamp-2">{s.context}</p>
            )}
            <div className="flex flex-wrap gap-1 mt-1.5">
              {summaryChips.map((c) => (
                <Badge key={c.label} variant={c.variant} className="text-[10px]">{c.label}</Badge>
              ))}
              {(note.tags || []).map((t) => (
                <Badge key={t} variant="outline" className="text-[10px]">#{t}</Badge>
              ))}
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={() => downloadNotePdf(note, schema)} data-testid={`pdf-${note.id}`} aria-label="Scarica PDF" title="Scarica PDF">
              <Download className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => setOpen((v) => !v)} data-testid={`toggle-${note.id}`} aria-label="Espandi">
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onDelete?.(note)} data-testid={`delete-${note.id}`} aria-label="Elimina">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>

        {open && (
          <div className="pt-2 border-t border-border space-y-2 text-sm">
            {textFields.map((k) => (s[k] ? (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{labelFor(k)}</div>
                <div>{s[k]}</div>
              </div>
            ) : null))}
            {listFields.map((k) => (Array.isArray(s[k]) && s[k].length > 0 ? (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{labelFor(k)}</div>
                <ul className="list-disc pl-4">
                  {s[k].map((item, i) => (<li key={`${k}-${i}`}>{item}</li>))}
                </ul>
              </div>
            ) : null))}
            {note.raw_transcript && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Trascrizione originale</summary>
                <div className="mt-1 whitespace-pre-wrap font-mono bg-muted/60 rounded p-2">{note.raw_transcript}</div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DiaryNoteCard;
